import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { verifyPasswordResetCode, confirmPasswordReset } from 'firebase/auth';
import { auth } from '../firebase';
import { Input } from '../components/ui/atoms/input/Input';
import { Button } from '../components/ui/atoms/Button/Button';
import { useToast } from '../context/ToastContext';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { showToast } = useToast() as any;

  const [oobCode, setOobCode] = useState<string | null>(null);
  const [validating, setValidating] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const code = searchParams.get('oobCode');
    const mode = searchParams.get('mode');
    if (!code || mode !== 'resetPassword') {
      setError('Invalid password reset link.');
      setValidating(false);
      return;
    }

    setOobCode(code);

    verifyPasswordResetCode(auth, code)
      .then((email) => {
        setEmail(email);
        setValidating(false);
      })
      .catch(() => {
        setError('Invalid or expired password reset link.');
        setValidating(false);
      });
  }, [searchParams]);

  const handleSubmit = async () => {
    if (newPassword.length < 6) {
      showToast({
        message: 'Password must be at least 6 characters.',
        type: 'error',
      });
      return;
    }
    if (newPassword !== confirmPassword) {
      showToast({ message: 'Passwords do not match.', type: 'error' });
      return;
    }

    setBusy(true);
    try {
      await confirmPasswordReset(auth, oobCode!, newPassword);
      showToast({ message: 'Password reset successful!', type: 'success' });
      navigate('/auth');
    } catch (err) {
      console.error('Password reset error:', err);
      showToast({
        message: 'Failed to reset password. Try again.',
        type: 'error',
      });
    } finally {
      setBusy(false);
    }
  };

  if (validating) {
    return (
      <div className="background-light-primary min-h-screen flex items-center justify-center px-4">
        <div className="text-center text-gray-600">Verifying link…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="background-light-primary min-h-screen flex items-center justify-center px-4">
        <div className="bg-white shadow-xl rounded-2xl w-full max-w-sm px-6 py-6 text-center space-y-3">
          <p className="text-red-600 font-semibold">{error}</p>
          <Button 
            variant="black" 
            className="bg-brandPurpleDark disabled:bg-brandPurpleLight disabled:text-brandPurpleDark"
            onClick={() => navigate('/forgot')}>
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="background-light-primary min-h-screen flex items-center justify-center px-4">
      <div className="bg-white shadow-xl rounded-2xl w-full max-w-sm px-6 py-6 space-y-4 text-center">
        <h4 className="text-xl font-semibold text-primary">Reset Password</h4>

        <Input
          type="password"
          placeholder="New password"
          value={newPassword}
          setValue={setNewPassword}
        />
        <Input
          type="password"
          placeholder="Confirm password"
          value={confirmPassword}
          setValue={setConfirmPassword}
        />

        <Button 
          variant="black" 
          disabled={busy} 
          className="bg-brandPurpleDark disabled:bg-brandPurpleLight disabled:text-brandPurpleDark"
          onClick={handleSubmit}>
          {busy ? 'Resetting...' : 'Set New Password'}
        </Button>
      </div>
    </div>
  );
}
